import { htmlToTiptapText } from '../helpers/htmlToTiptapText';
import { getTiptapTextRange } from '../helpers/getTiptapTextRange';

/**
 *
 * @param highlight
 * @param options
 * @param options.blur Skip highlight-within-selection-range check
 * @param options.focus
 */
export function addHighlight(highlight, { blur, focus } = {}) {
  return ({ editor, chain }) => {
    const {
      id, highlightedText, occurrence, className, action, type,
    } = highlight;

    this.storage.highlights[id] = highlight;

    if (this.storage.highlights[id]?.hide) {
      return chain();
    }

    const { from, to } = getTiptapTextRange(
      { html: editor.getHTML(), lookupText: highlightedText, occurrence },
    );
    if (from === to) {
      return chain();
    }

    // Hide highlights within selection range
    const { anchor, head } = editor.view.state.selection;
    const selectionStart = Math.min(anchor, head);
    const selectionEnd = Math.max(anchor, head);
    const isBeforeSelection = to <= selectionStart;
    const isAfterSelection = from >= selectionEnd;

    if (!blur && focus && anchor !== head && !(isBeforeSelection || isAfterSelection)) {
      if (action !== 'insert_before_hl' && action !== 'insert_after_hl') {
        // trigger mouseOut when a highlight gets hidden
        this.options.onMouseOut({ id, event: null, tiptap: this.editor });
        return chain();
      }
    }

    let selection = { from, to };

    // only highlight first character (before_hl) or last character (after_hl) to align tooltip
    if (type === 'title') {
      if (['insert_before_hl', 'insert_after_hl'].includes(action)) {
        selection = {
          insert_before_hl: { from, to: from + 1 },
          insert_after_hl: { to, from: to - 1 },
        }[action];
      }
    }

    return chain()
      .setTextSelection(selection)
      .setMark('highlightSuggestion', { id, className: `${className} ${action}` });
  };
}

/**
 *
 * @param highlights
 * @param options
 * @param options.blur Skip highlight-within-selection-range check
 * @param options.focus
 */
export function setHighlights(highlights = [], { blur, focus } = {}) {
  return ({ chain, editor }) => {
    const { anchor, head } = editor.view.state.selection;

    chain().setMeta('addToHistory', false).setMeta('contentNotUpdated', true);
    chain().removeAllHighlights();

    highlights.forEach((highlight) => {
      chain().addHighlight(highlight, { blur, focus });
    });

    chain().setTextSelection({ from: anchor, to: head });
  };
}

/**
 *
 */
export function removeAllHighlights() {
  return ({ chain }) => chain()
    .setTextSelection({ from: 1, to: 10e6 })
    .unsetMark('highlightSuggestion');
}

/**
 *
 * @param root0
 * @param root0.id
 * @param root0.suggestion
 */
export function replaceHighlight({ id, suggestion = '' }) {
  return ({ editor, chain }) => {
    const highlight = this.storage.highlights[id];
    if (!highlight) {
      return chain();
    }

    const html = editor.getHTML();

    const {
      from, to, charBefore, charAfter,
    } = getTiptapTextRange({
      html, lookupText: highlight.highlightedText, occurrence: highlight.occurrence,
    });

    chain().setTextSelection({ from, to }).unsetMark('highlightSuggestion');

    highlight.hide = true;

    if (suggestion) {
      if (highlight.action === 'insert_after_hl') {
        chain().insertContentAt(to, ` ${suggestion}`);
      } else if (highlight.action === 'insert_before_hl') {
        chain().insertContentAt(from, `${suggestion} `);
      } else {
        chain().insertContentAt({ from, to }, suggestion);
      }
    } else {
      // Check if first letter of highlight is capitalized and handle capitalizing the next word
      const firstLetter = highlight.highlightedText.charAt(0);
      const text = htmlToTiptapText(html);
      const fromWithOrWithoutSpace = charBefore === ' ' ? from - 1 : from;

      /* Handles situations where next word isn't a letter
      and when the highlight is a capitalized word in the middle of a sentence */
      const shouldCapitalize = firstLetter.toUpperCase() === firstLetter
        && text.charAt(to).match(/[a-z]/i)
        && !text.charAt(fromWithOrWithoutSpace - 2).match(/[a-z,;'^~\-_%+=@#$&*([{:<>))`"\]}]/i);

      if (shouldCapitalize) {
        chain().insertContentAt({
          from,
          to: charAfter === ' ' ? to + 2 : to + 1,
        }, text.charAt(to).toUpperCase());
      } else {
        chain().deleteRange({
          from: fromWithOrWithoutSpace,
          to,
        });
      }
    }

    return chain().setTextSelection(from).focus();
  };
}
